import React, { useEffect, useMemo, useContext } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { ModalHeader } from 'shared/ModalHeader';
import TextSnippetWrapper from 'components/TextSnippetWrapper';
import { useForm, Controller } from 'react-hook-form';
import { RequiredValidation } from 'components/UI/RequiredValidation/RequiredValidation';
import { transferTrustFund } from 'services/billingServices';
import { confirmDialog } from 'primereact/confirmdialog';
import { useCaseAndLeadsList } from 'hooks/useCaseAndLeadList';
import { useAccounts } from 'hooks/useAccounts';
import GlobalLoader from 'components/GlobalLoader/GlobalLoader';
import { UserDetailsContext } from 'context/userDetailsContext';
import TimezoneDatePicker from 'components/common/TimezoneDatePicker/TimezoneDatePicker';

export const TransferFundModalV2 = ({ onHide, caseDetails, callback, trustBalance }) => {
  const { accountList } = useAccounts();
  const { caseAndLeadsList } = useCaseAndLeadsList(caseDetails?.case_id);
  const { addToast } = useToast();
  const userContext = useContext(UserDetailsContext);

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      amount: undefined,
      refundDate: undefined,
      paymentMethod: undefined,
      notes: '',
    },
  });

  const onChangeRefundFull = (e) => {
    if (e.target.checked && trustBalance > 0) {
      setValue('amount', parseFloat(trustBalance), { shouldValidate: true });
    }
    if (!e.target.checked) {
      setValue('amount', undefined);
    }
  };

  const onSubmit = (formData) => {
    confirmDialog({
      message: 'Editing and deleting options will be disabled for all trust transactions. Are you sure you want to proceed?',
      header: 'Warning',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        const data = {
          case_id: caseDetails?.case_id,
          case_name: caseDetails?.case_description?.case_name,
          deposit_into: formData?.deposit_info?.account_type,
          deposit_account_name: formData?.deposit_info?.account_name,
          amount: formData?.amount,
          payment_record_amount: formData?.amount,
          payment_record_date: formData?.refundDate,
          payment_record_type: 'TRUST',
          deposit_account_id: formData?.deposit_info?.account_id,
          debit_account_name: caseDetails?.default_trust_account?.[0]?.account_name,
          debit_account_id: caseDetails?.default_trust_account?.[0]?.account_id,
          payment_method: 'Transfer',
          description: formData?.notes,
        };
        transferTrustFund(data)
          .then((response) => {
            if (response?.status === 200 || response?.status === 206) {
              addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.TRANSFER_FUND_SUCCESS);
              typeof callback === 'function' && callback(true);
              onHide();
            }
          })
          .catch((error) => {
            console.log('error', error);
            addToast(
              false,
              toastConstant.toasterType.ERROR,
              toastConstant.api.FAILED,
              error?.response?.data?.message || toastConstant.message.TRANSFER_FUND_FAILED
            );
          });
      },
      reject: () => null,
    });
  };

  const amount = watch('amount');

  const trustAccount = useMemo(() => {
    let filteredAccounts = accountList?.filter((a) => a?.account_type === 'trust');
    if (caseAndLeadsList?.case_list?.[0]?.default_trust_account?.[0]?.account_id) {
      filteredAccounts = filteredAccounts?.filter(
        (account) => account?.account_id === caseAndLeadsList?.case_list?.[0]?.default_trust_account?.[0]?.account_id
      );
    } else if (caseAndLeadsList?.lead_list?.[0]?.default_trust_account?.[0]?.account_id) {
      filteredAccounts = filteredAccounts?.filter(
        (account) => account?.account_id === caseAndLeadsList?.lead_list?.[0]?.default_trust_account?.[0]?.account_id
      );
    }

    return filteredAccounts || [];
  }, [accountList, caseAndLeadsList]);

  const operatingAccounts = useMemo(() => {
    let filteredAccounts = accountList?.filter((a) => a?.account_type === 'operating');
    if (caseAndLeadsList?.case_list?.[0]?.default_operating_account?.[0]?.account_id) {
      filteredAccounts = filteredAccounts?.filter(
        (account) => account?.account_id === caseAndLeadsList?.case_list?.[0]?.default_operating_account?.[0]?.account_id
      );
    } else if (caseAndLeadsList?.lead_list?.[0]?.default_operating_account?.[0]?.account_id) {
      filteredAccounts = filteredAccounts?.filter(
        (account) => account?.account_id === caseAndLeadsList?.lead_list?.[0]?.default_operating_account?.[0]?.account_id
      );
    }
    return filteredAccounts || [];
  }, [accountList, caseAndLeadsList]);

  useEffect(() => {
    if (trustAccount?.length === 1) {
      setValue('trust', trustAccount[0], { shouldValidate: true });
    }

    if (operatingAccounts?.length === 1) {
      setValue('deposit_info', operatingAccounts[0], { shouldValidate: true });
    }
  }, [trustAccount, operatingAccounts, setValue]);

  return (
    <>
      <GlobalLoader />
      <Dialog header={ModalHeader} visible onHide={onHide} className="create-msg-popup" closable={false}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="row">
            <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
              <div className="modal-heading F-size16 text-bold">Transfer Fund</div>
            </div>

            <div className="d-flex align-items-top py-2 flex-wrap">
              <div className="col-12 col-sm-4 pt-2">
                Amount<span className="text-danger">*</span>
              </div>
              <div className="col-12 col-sm-8">
                <Controller
                  name="amount"
                  control={control}
                  rules={{
                    validate: (value) => {
                      if (value === null || value === undefined || value === '') {
                        return 'Required Valid Amount';
                      }
                      const numericValue = parseFloat(value);
                      if (isNaN(numericValue)) return 'Must be a number';
                      if (numericValue <= 0) return 'Must be greater than 0';
                      if (numericValue > parseFloat(trustBalance)) return 'Cannot be greater than available trust balance';

                      return true;
                    },
                  }}
                  render={({ field: { name, onChange, value } }) => (
                    <InputNumber
                      name={name}
                      value={value}
                      onChange={(e) => onChange(e.value)}
                      className="input-shadow w-100"
                      mode="currency"
                      currency="USD"
                      locale="en-US"
                    />
                  )}
                />
                <RequiredValidation field="amount" errors={errors} />
                <div className="mt-3">
                  <label className="switch">
                    <input type="checkbox" checked={amount === parseFloat(trustBalance)} onChange={onChangeRefundFull} />
                    <span className="slider round"></span>
                  </label>
                  <span className="ms-2">Transfer full amount</span>
                </div>
              </div>
            </div>

            <div className="d-flex align-items-center py-2">
              <div className="col-12 col-sm-4 p-medium">
                Trust<span className="text-danger">*</span>
              </div>
              <div className="col-12 col-sm-8">
                <Controller
                  name="trust"
                  control={control}
                  rules={{ required: 'Required' }}
                  render={({ field }) => (
                    <Dropdown
                      className="w-100"
                      options={trustAccount}
                      onChange={(e) => field.onChange(e.value)}
                      optionLabel={'account_name'}
                      filterBy="name"
                      placeholder={'Select Trust'}
                      value={field.value}
                      filter
                    />
                  )}
                />
                <RequiredValidation field="trust" errors={errors} />
              </div>
            </div>

            <div className="d-flex align-items-center py-2">
              <div className="col-12 col-sm-4 p-medium">
                Deposit Info<span className="text-danger">*</span>
              </div>
              <div className="col-12 col-sm-8">
                <Controller
                  name="deposit_info"
                  control={control}
                  rules={{ required: 'Required' }}
                  render={({ field }) => (
                    <Dropdown
                      className="w-100"
                      options={operatingAccounts}
                      onChange={(e) => field.onChange(e.value)}
                      optionLabel={'account_name'}
                      filterBy="name"
                      placeholder={'Select Deposit Info'}
                      value={field.value}
                      filter
                    />
                  )}
                />
                <RequiredValidation field="deposit_info" errors={errors} />
              </div>
            </div>

            <div className="d-flex align-items-center py-2">
              <div className="col-12 col-sm-4">
                Date<span className="text-danger">*</span>
              </div>
              <div className="col-12 col-sm-8">
                <Controller
                  name="refundDate"
                  control={control}
                  rules={{ required: 'Required' }}
                  render={({ field: { value, onChange } }) => (
                    <TimezoneDatePicker
                      value={value}
                      onChange={onChange}
                      className="input-shadow w-100"
                      timezone={userContext?.userDetails?.timezone}
                      isEndOfDay={true}
                    />
                  )}
                />
                <RequiredValidation field="refundDate" errors={errors} />
              </div>
            </div>

            <div className="d-flex align-item-center py-2">
              <div className="col-12 col-sm-4">Notes</div>
              <div className="col-12 col-sm-8">
                <Controller
                  name="notes"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <TextSnippetWrapper
                      value={value}
                      onChange={onChange}
                      type="textarea"
                      className="input-shadow w-100 p-2"
                      autoComplete="off"
                    />
                  )}
                />
              </div>
            </div>
          </div>
          <hr />
          <div className="row">
            <div className="col-12 d-flex justify-content-end mt-2">
              <Button className="p-button-secondary outline" label="Cancel" type="button" onClick={onHide} />
              <Button className="p-button-secondary ms-2" label="Transfer" type="submit" />
            </div>
          </div>
        </form>
      </Dialog>
    </>
  );
};
